<template>
  <div id="memorial-show">
    <loader v-if="!entity" class="mt-5" />
    <template v-else>
      <disabled-overlay />
      <security-overlay
        :memorial="entity"
      />
      <profile-module />
      <h3>{{ $t('pages.memorial.biography.title') }}</h3>
      <div v-html="$helper.demoFallbackTranslation(entity, 'biography')" />
      <h3>{{ $t('pages.memorial.map.title') }}</h3>
      <div class="map">
        <accessibility-map :can-update-pointer="false" />
      </div>
      <p>{{ $helper.demoFallbackTranslation(entity, 'localisation_phrase') }}</p>
      <br>
      <b-row v-if="!$root.canManage()">
        <b-col cols="12" md="6" offset-md="3">
          <a
            :href="`mailto:${contact_mail}`"
            target="_blank"
            class="btn btn-info btn-block"
            v-html="$t('pages.memorial.contact_prescriber')"
          />
        </b-col>
      </b-row>
      <br>
      <br>
      <div class="life-steps">
        <e-index
          :uri="$helper.getUri('step')"
          :modal-uri="`api/memorial/${id}/step`"
          :order-call-back="orderCallBack"
          :can-create="$root.canManage()"
          :can-manage="$root.canManage()"
          name="step"
        >
          <div slot="list-content" slot-scope="ctx">
            <span class="red-text step-title">
              {{ ctx.item.date | moment('LL') }} : {{ $helper.demoFallbackTranslation(ctx.item, 'title') }}
            </span>
            <div class="clearfix" />
            <b-row>
              <b-col sm="8" v-html="$helper.demoFallbackTranslation(ctx.item, 'text')" />
              <b-col v-if="ctx.item.image" sm="4">
                <div
                  :style="`background-image:url(${imgurl(ctx.item.image.file_url, 'full')})`"
                  class="thumbnail clickable"
                  @click="openModal(modalName('image'), ctx.item)"
                >
                  <i class="ti ti-fw ti-2x ti-search" />
                </div>
              </b-col>
            </b-row>
          </div>
        </e-index>
      </div>
      <e-modal
        :event-id="modalName('image')"
        :title="$t(title)"
      >
        <img :src="image" class="img-fluid" >
      </e-modal>
    </template>
  </div>
</template>

<script>
  import Loader from '@/components/Loader';
  import EIndex from '@/components/EIndex';
  import EModal from '@/components/EModal';
  import AjaxEdit from '@triotech/vue-core/src/components/ajax/AjaxEdit';
  import ProfileModule from '@/components/ProfileModule';
  import DisabledOverlay from '@/components/DisabledOverlay';
  import SecurityOverlay from '@/components/SecurityOverlay';
  import AccessibilityMap from '@/components/AccessibilityMap';

  export default {
    name: 'MemorialShow',
    components: {
      Loader,
      EIndex,
      EModal,
      AjaxEdit,
      ProfileModule,
      DisabledOverlay,
      SecurityOverlay,
      AccessibilityMap,
    },
    props: {
      id: {
        type: [Number, String],
        default: null,
      },
    },
    data() {
      return {
        entity: null,
        // eslint-disable-next-line no-underscore-dangle
        uid: this._uid,
        image: '',
        title: '',
        name: 'memorial',
        uri: 'api/memorial',
        additionnal_route: 'style',
        contact_mail: '',
      };
    },
    async mounted() {
      this.entity = await this.getMemorial();
      this.contact_mail = this.entity.manager.email;
      this.set({ memorial: this.entity });

      this.$bus.$emit('t-event.get-memo.stored', this.entity);

      this.$bus.$on(`t-event.t-modal.image-${this.uid}.closed`, () => {
        this.image = '';
      });

      this.$bus.$on('t-event.edit-submit.memorial.success', async () => {
        this.entity = await this.getMemorial();
        this.set({ memorial: this.entity });
      });
    },
    destroyed() {
      this.$bus.$off([
        `t-event.t-modal.image-${this.uid}.closed`,
        `t-event.t-modal.memorial_style-${this.uid}.close`,
        't-event.edit-submit.memorial.success',
      ]);
    },
    methods: {
      modalName(action) {
        return `${action}-${this.uid}`;
      },
      openModal(id, data = null) {
        if (id === this.modalName('image')) {
          this.image = this.imgurl(data.image.file_url, 'full');
          this.title = data.title;
        }
        this.$bus.$emit(`t-event.t-modal.${id}.open`, data);
      },
      async getMemorial() {
        const identifier = this.$route.params.id;
        let memorial;
        await this.$ajax.get(`api/public/memorial/${identifier}`).then((memo) => {
          memorial = memo;
          this.$bus.$emit('t-event.get-memo.success', memorial);
        });

        return memorial;
      },
      orderCallBack(collection) {
        return _.orderBy(collection, ['date'], ['asc']);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '~@/scss/vars';
  @import '~@/scss/media';

  #overlay {
    background: rgba($black, .7);
    position: fixed;
    z-index: 99;
    left: 0;
    top: 56px;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;

    form {
      margin: auto;

      p {
        color: $white;
      }
    }
  }

  /deep/ .profile-header {
    min-height: 450px !important;

    .cover-img {
      height: 400px !important;
    }

    .map {
      height: 150px !important;
      bottom: 60px !important;
    }

    .profile-pic {
      bottom: 0 !important;
    }

    .info {
      bottom: 80px !important;
    }
  }

  .life-steps {
    /deep/ .list-group {
      position: relative;
      margin-top: 2em;

      &::before {
        content: '';
        position: absolute;
        height: 100%;
        width: 2px;
        background-color: $light-grey;
        left: 0;
        margin: auto;
      }

      .list-group-item {
        margin: 1em 0 1em 3%;
        width: 97%;
        position: relative;

        p {
          color: $black;
        }

        &::before {
          content: '';
          position: absolute;
          height: 20px;
          width: 20px;
          background-color: $primary;
          border-radius: 50%;
          top: 0;
          left: calc(-3% - 11px);
        }
      }
    }
  }

  /deep/ .modal-form {
    overflow: auto;

    .sweet-modal {
      top: 55%;
      margin-bottom: 25px;
    }
  }

  @include editable-max-width(765px) {
    /deep/ .profile-header {
      min-height: 200px !important;

      .cover-img {
        height: 250px !important;
      }

      .info {
        padding-top: 15px;
        bottom: 10px !important;
      }

      .profile-pic-container {
        bottom: 85px !important;
      }
    }
  }

  @include editable-max-width(600px) {
    /deep/ .modal-form {
      .sweet-modal {
        top: $nav-height;
        max-height: none;
        height: auto;
        margin-bottom: 0;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/Memorial/show.vue